import { format } from 'date-fns';
import flatten from 'lodash-es/flatten';
import forOwn from 'lodash-es/forOwn';
import * as moment from 'moment';
import { createSelector } from 'reselect';

import type APIMessage from '~/types/APIMessage';
import type Order from '~/types/Order';
import type Rate from '~/types/Rate';
import type Shipment from '~/types/Shipment';
import type Transaction from '~/types/Transaction';

import { FAILED_ADDRESS_VALIDATION } from '~/constants/errors/apiMessageErrorCodes';
import hasRatingErrors from '~/pages/Orders/All/OrderDetailsPage/RatingErrors/hasRatingErrors';
import { orderHasShipmentDateError } from '~/pages/Orders/Blackbeard/Drawers/LabelPurchaseDrawer/orderErrors';
import { type SelectedRates } from '~/reducers/rates/rates.reducer';
import {
  getBatchEntriesByOrderId,
  getBatchEntryById,
  getBatchEntryData,
  makeGetBatchEntryById,
  makeGetBatchEntryByIds,
} from '~/selectors/batch-entry.selectors';
import { getBatchEntryIdFromProps, getBatchEntryIdsFromProps } from '~/selectors/props.selectors';
import { type ApplicationState } from '~/store';
import { messageIsVisibleForShipment } from '~/types/APIMessage';
import {
  areAddressesFullyEqual,
  areAllAddressesEqual,
  isDomesticUSShipment,
  type BaseAddress,
} from '~/types/Address';
import {
  type IncompleteRegStatus,
  type RegStatusByCarrierAccount,
} from '~/types/CarrierAccountRegStatus';
import {
  type OrderObjectId,
  type OrderSelection,
  type OrdersRenderData,
  type SelectedOrdersAddresses,
} from '~/types/Order';
import {
  getBatchEntriesIdsSortedByRank,
  isReadyForPurchase,
  type BatchEntryExtended,
} from '~/types/OrderBatchEntry';
import { BatchEntryStatus, type BatchEntryId } from '~/types/OrdersBatch';
import { TransactionStatus } from '~/types/Transaction';

import { newRelic } from '../utils/newRelic/newRelic';
import { getCarrierAccountRegStatusesIncomplete } from './carrier-accounts.selectors';
import {
  getIsUpdatingOrder,
  getOrders,
  getOrdersData,
  getSelectedCount,
  getSelectedOrders,
  getTotalOrders,
} from './orders.selectors';
import { getFailedBatchEntriesMessages } from './orders-batch.selectors';
import { getRatesData, getRatesIdsByShipmentId, getSelectedRates } from './rates.selectors';
import { getShipmentLoadingStates, getShipmentsData } from './shipments.selectors';
import { getTransactionsData } from './transactions.selectors';
import {
  getIsUserAddressValid,
  getUserAddressIfValid,
  getUserReturnAddressIfValid,
} from './user.selectors';

export const makeGetShipmentIsLoadingFromBatchEntries = () => {
  const getBatchEntryByIds = makeGetBatchEntryByIds();

  return createSelector(
    getBatchEntryByIds,
    getShipmentLoadingStates,
    (batchEntries, shipmentLoadingStates): boolean =>
      batchEntries
        ?.map((b) => {
          if (!b || !b.shipmentObjectId) return false;
          // There's a shipment object id but the fetch wasn't triggered yet
          if (!shipmentLoadingStates.hasOwnProperty(b.shipmentObjectId)) return true;

          return !!shipmentLoadingStates[b.shipmentObjectId];
        })
        .some((b) => b) || false,
  );
};

export const batchEntryShipmentIsLoading = (
  shipmentLoadingStates: Partial<Record<string, boolean>>,
  batchEntry?: BatchEntryExtended,
): boolean => {
  if (!batchEntry || !batchEntry.shipmentObjectId) return false;

  // There's a shipment object id but the fetch wasn't triggered yet
  if (!shipmentLoadingStates.hasOwnProperty(batchEntry.shipmentObjectId)) return true;

  return !!shipmentLoadingStates[batchEntry.shipmentObjectId];
};

export const makeGetShipmentIsLoadingFromBatchEntry = () => {
  const getBatchEntryById = makeGetBatchEntryById();

  return createSelector(
    getBatchEntryById,
    getShipmentLoadingStates,
    (batchEntry, shipmentLoadingStates): boolean =>
      batchEntryShipmentIsLoading(shipmentLoadingStates, batchEntry || undefined),
  );
};

export const getShipmentIsLoadingFromBatchEntry = createSelector(
  getBatchEntryById,
  getShipmentLoadingStates,
  (batchEntry, shipmentLoadingStates): boolean => {
    if (!batchEntry || !batchEntry.shipmentObjectId) return false;

    // There's a shipment object id but the fetch wasn't triggered yet
    if (!shipmentLoadingStates.hasOwnProperty(batchEntry.shipmentObjectId)) return true;

    return !!shipmentLoadingStates[batchEntry.shipmentObjectId];
  },
);

export const getRatesForBatchEntry = createSelector(
  getBatchEntryById,
  getRatesData,
  getRatesIdsByShipmentId,
  (batchEntry, activeRates, ratesByShipment) => {
    if (!batchEntry || !batchEntry.shipmentObjectId) return [];
    const rateIds = ratesByShipment[batchEntry.shipmentObjectId] || [];

    return rateIds.map((rateId) => activeRates[rateId]);
  },
);

export const getSelectedRateBatchEntry = createSelector(
  getBatchEntryById,
  getRatesData,
  getSelectedRates,
  (batchEntry, activeRates, selectedRates): Maybe<Rate> => {
    if (!batchEntry || !batchEntry.shipmentObjectId) return null;

    const selectedRate = selectedRates[batchEntry.shipmentObjectId];

    return selectedRate ? activeRates[selectedRate] : null;
  },
);

/**
 * Map of batchEntryId -> currently selected Rate, for every batch entry that has one. Used to
 * capture the user's chosen rate before an address bypass regenerates the shipment, so it can be
 * restored on the new shipment
 */
export const getSelectedRatesByBatchEntryId = createSelector(
  getBatchEntryData,
  getRatesData,
  getSelectedRates,
  (batchEntries, activeRates, selectedRates): Record<number, Rate> =>
    Object.values(batchEntries).reduce<Record<number, Rate>>((accum, batchEntry) => {
      if (!batchEntry.shipmentObjectId) return accum;
      const rateId = selectedRates[batchEntry.shipmentObjectId];
      const rate = rateId ? activeRates[rateId] : undefined;
      if (rate) accum[batchEntry.id] = rate;
      return accum;
    }, {}),
);

export const getSelectedBatchEntries = createSelector(
  getSelectedOrders,
  (selectedOrders): number[] => flatten(Object.values(selectedOrders)),
);

const getBatchEntriesDataForSelectedOrders = createSelector(
  getSelectedBatchEntries,
  getBatchEntryData,
  (batchEntryIds, batchEntries): BatchEntryExtended[] =>
    batchEntryIds.map((batchEntryId) => batchEntries[batchEntryId]).filter((v) => v),
);

const getSelectedBatchEntriesShipmentsMessagesWithoutHidden = createSelector(
  getSelectedOrders,
  getOrdersData,
  getBatchEntryData,
  getShipmentsData,
  (selectedOrders, ordersData, batchEntryData, shipmentData): APIMessage[] =>
    Object.entries(selectedOrders ?? {}).flatMap(([orderObjectId, batchEntryIds]) => {
      const order = ordersData[orderObjectId];

      return (batchEntryIds ?? []).flatMap((batchEntryId) => {
        const batchEntry = batchEntryData[batchEntryId];
        const shipment: Shipment | undefined = batchEntry?.shipmentObjectId
          ? shipmentData[batchEntry.shipmentObjectId]
          : undefined;
        if (!shipment) return [];

        // Fall back to the order's addresses when the batch entry has none, mirroring getAddresses.
        const addressFrom = batchEntry?.addressFrom ?? order?.addressFrom;
        const addressTo = batchEntry?.addressTo ?? order?.toAddress;
        const isDomestic = isDomesticUSShipment(addressFrom, addressTo);

        return shipment.messages.filter((message) =>
          messageIsVisibleForShipment(message, { isDomestic }),
        );
      });
    }),
);

// this method uses an object to filter unique messages by carrier, then converts them back to a flat list
export const getUniqueSelectedBatchEntriesShipmentsMessages = createSelector(
  getSelectedBatchEntriesShipmentsMessagesWithoutHidden,
  (messages: APIMessage[]): APIMessage[] => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const messagesByCarrier: Record<string, any> = {};
    messages.forEach((message) => {
      const { source, text } = message;
      const carrierMessages = messagesByCarrier[source as keyof typeof messagesByCarrier];
      if (!carrierMessages) {
        messagesByCarrier[source as keyof typeof messagesByCarrier] = {
          // the code value is not guarenteed to exist, so the only unique
          // data we have to key off of is the actual message. We should move
          // to using the code once they are peroperly enforced and unique
          [text]: message,
        };
      } else {
        carrierMessages[text as keyof typeof carrierMessages] = message;
      }
    });

    const uniqueMessagesArray: APIMessage[] = [];
    forOwn(messagesByCarrier, (messages) => {
      forOwn(messages, (message) => {
        uniqueMessagesArray.push(message);
      });
    });

    return uniqueMessagesArray;
  },
);

// Gets the sender/return addresses of the order selection, prioritizing the batch entries' addresses over the orders' addresses
export const deriveAddressesFromOrderSelection = (
  orderSelection: OrderSelection,
  ordersData: HashMap<string, Order>,
  batchEntryData: HashMap<number, BatchEntryExtended>,
  defaultSenderAddress: Maybe<BaseAddress>,
  defaultReturnAddress: Maybe<BaseAddress>,
): SelectedOrdersAddresses =>
  Object.entries(orderSelection).reduce<{
    returnAddresses: BaseAddress[];
    senderAddresses: BaseAddress[];
  }>(
    (addresses, [orderId, batchEntries]) => {
      const order: Order | undefined = ordersData[orderId]; // This could potentially be undefined

      if (batchEntries.length > 0) {
        // If order selection has batch entries, look at each batch entry's sender/return

        batchEntries.forEach((id) => {
          const batchEntry = batchEntryData[id]; // This could potentially be undefined

          // Gets sender/return from batch entry, fallback on order, then fallback on user defaults
          const senderAddress =
            batchEntry?.addressFrom || order?.addressFrom || defaultSenderAddress;
          const returnAddress =
            batchEntry?.addressReturn || order?.addressReturn || defaultReturnAddress;

          if (
            senderAddress &&
            addresses.senderAddresses.every(
              (address) => !areAddressesFullyEqual(senderAddress, address),
            )
          )
            addresses.senderAddresses.push(senderAddress);

          if (
            returnAddress &&
            addresses.returnAddresses.every(
              (address) => !areAddressesFullyEqual(returnAddress, address),
            )
          )
            addresses.returnAddresses.push(returnAddress);
        });
      } else {
        // Otherwise if the order selection does not have any batch entries, then look at the order's sender/return

        // Get sender/return from order, fallback on user defaults
        const senderAddress = order?.addressFrom || defaultSenderAddress;
        const returnAddress = order?.addressReturn || defaultReturnAddress;

        if (
          senderAddress &&
          addresses.senderAddresses.every(
            (address) => !areAddressesFullyEqual(senderAddress, address),
          )
        )
          addresses.senderAddresses.push(senderAddress);

        if (
          returnAddress &&
          addresses.returnAddresses.every(
            (address) => !areAddressesFullyEqual(returnAddress, address),
          )
        )
          addresses.returnAddresses.push(returnAddress);
      }

      return addresses;
    },
    { returnAddresses: [], senderAddresses: [] },
  );

export const getAddressesFromOrderSelection = createSelector(
  getSelectedOrders,
  getOrdersData,
  getBatchEntryData,
  getUserAddressIfValid,
  getUserReturnAddressIfValid,
  deriveAddressesFromOrderSelection,
);

export const getAreAllSenderReturnAddressesEqual = createSelector(
  getAddressesFromOrderSelection,
  (addressesFromOrderSelection): boolean =>
    areAllAddressesEqual(
      addressesFromOrderSelection.senderAddresses.concat(
        addressesFromOrderSelection.returnAddresses,
      ),
    ),
);

export const getIsMultipleSenderAddressesSelected = createSelector(
  getAddressesFromOrderSelection,
  (addressesFromOrderSelection): boolean => addressesFromOrderSelection.senderAddresses.length > 1,
);

export const getIsMultipleReturnAddressesSelected = createSelector(
  getAddressesFromOrderSelection,
  (addressesFromOrderSelection): boolean => addressesFromOrderSelection.returnAddresses.length > 1,
);

export const getShipmentByBatchEntry = createSelector(
  getShipmentsData,
  getBatchEntryById,
  (shipmentsData, batchEntry): Maybe<Shipment> =>
    batchEntry && batchEntry.shipmentObjectId
      ? shipmentsData[(batchEntry as BatchEntryExtended).shipmentObjectId!]
      : null,
);

const batchEntriesToBuy = (
  batchEntryIds: number[],
  entriesData: HashMap<number, BatchEntryExtended>,
): BatchEntryExtended[] =>
  batchEntryIds
    .map((batchEntryId) => entriesData[batchEntryId])
    .filter((v) => v) as BatchEntryExtended[];

const getBatchEntriesToBuy = createSelector(
  getBatchEntryIdsFromProps,
  getBatchEntryData,
  batchEntriesToBuy,
);

const validBatchEntriesToBuy = (
  batchEntries: BatchEntryExtended[],
  selectedRates: SelectedRates,
): BatchEntryExtended[] =>
  batchEntries.filter(
    (batchEntry) =>
      isReadyForPurchase(batchEntry) &&
      !!selectedRates[batchEntry.shipmentObjectId!] &&
      !orderHasShipmentDateError(batchEntry),
  );

// This works nice for order screen/checkout flow
export const getValidBatchEntriesToBuy = createSelector(
  getBatchEntriesToBuy,
  getSelectedRates,
  validBatchEntriesToBuy,
);

export const getValidBatchEntriesToBuyCount = createSelector(
  getValidBatchEntriesToBuy,
  (validBatchEntriesToBuy): number => validBatchEntriesToBuy.length || 0,
);

export const getExcludedBatchEntriesToBuyCount = createSelector(
  getSelectedCount,
  getValidBatchEntriesToBuyCount,
  (selectedCount, validCount): number => selectedCount - validCount,
);

export const getIsBatchEntryReadyForCheckout = createSelector(
  getBatchEntryById,
  getSelectedRates,
  getIsUpdatingOrder,
  (batchEntry, selectedRates, isUpdatingOrder): boolean => {
    if (!batchEntry) return false;

    return (
      !!selectedRates[batchEntry.shipmentObjectId!] &&
      isReadyForPurchase(batchEntry) &&
      !isUpdatingOrder
    );
  },
);

export const getRatesForBatchEntriesToBuy = createSelector(
  getBatchEntriesToBuy,
  getSelectedRates,
  getRatesData,
  (batchEntries, selectedRates, activeRates): Rate[] =>
    batchEntries
      .map((batchEntry) => {
        if (!batchEntry.shipmentObjectId || batchEntry.transactionObjectId) return null;
        const rateId = selectedRates[batchEntry.shipmentObjectId];

        return activeRates[rateId];
      })
      .filter((v): v is Rate => !!v),
);

export const getReturnsCountForBatchEntriesToBuy = createSelector(
  getBatchEntriesToBuy,
  (batchEntries): number =>
    batchEntries.reduce((count, batchEntry) => {
      if (batchEntry.extras && batchEntry.extras.isReturnRequested) return count + 1;

      return count;
    }, 0),
);

export const makeGetBatchEntryTransactionFromOrder = () => {
  const getBatchEntryById = makeGetBatchEntryById();

  return createSelector(
    getBatchEntryById,
    getOrdersData,
    (batchEntry, ordersData): Maybe<Transaction> => {
      if (!batchEntry || !ordersData) return null;
      const order = ordersData[batchEntry.orderObjectId];

      return (
        (order &&
          batchEntry &&
          order.transactions &&
          order.transactions.find(
            (transaction) => transaction.objectId === batchEntry.transactionObjectId,
          )) ||
        null
      );
    },
  );
};

export const isBatchEntryTransactionSuccess = (
  order: Order,
  batchEntry: BatchEntryExtended,
  transactions: HashMap<string, Transaction>,
): boolean => {
  const transactionId = batchEntry.transactionObjectId;
  const transactionFromBatchEntry = transactionId ? transactions[transactionId] : null;
  const transaction = order?.transactions?.find(
    (transaction) => transaction.objectId === transactionId,
  );

  return (transactionFromBatchEntry || transaction)?.status === TransactionStatus.SUCCESS;
};

export const getIsBatchEntryTransactionSuccess = createSelector(
  getBatchEntryIdFromProps,
  getBatchEntryData,
  getOrdersData,
  getTransactionsData,
  (batchEntryId, batchEntries, orders, transactions): boolean => {
    const batchEntry = batchEntries[batchEntryId];
    if (!batchEntry) return false;

    const order = orders[batchEntry.orderObjectId];

    return isBatchEntryTransactionSuccess(order, batchEntry, transactions);
  },
);

export const getSelectedOrdersMostCommonShipmentDate = createSelector(
  getBatchEntriesDataForSelectedOrders,
  (batchEntries): Date => {
    const dateCounts = batchEntries
      .map((batchEntry) =>
        batchEntry.shipmentDate ? batchEntry.shipmentDate!.toDate() : new Date(),
      )
      .reduce<Record<string, number>>((acc, date) => {
        const formattedDate = format(date, 'MM/dd/yy');
        return { ...acc, [formattedDate]: (acc[formattedDate] || 0) + 1 };
      }, {});

    const mostCommon = Object.keys(dateCounts).reduce(
      (a, b) => (dateCounts[a] > dateCounts[b] ? a : b),
      format(new Date(), 'MM/dd/yy'),
    );

    return new Date(mostCommon);
  },
);

export const getOrdersTableData = createSelector(
  getOrders,
  getBatchEntryData,
  getBatchEntriesByOrderId,
  (orders, batchEntriesData, batchEntriesByOrderId): OrdersRenderData[] =>
    orders.reduce((data: OrdersRenderData[], order: Order) => {
      const bes = batchEntriesByOrderId[order.objectId] || [];

      const batchEntries = bes.map((beId) => batchEntriesData[beId]).filter(Boolean);
      const notReturnBatchEntries = batchEntries.filter(({ isReturn }) => !isReturn);
      const notReturnBatchEntriesIds = notReturnBatchEntries.map(({ id }) => id);

      // Single order, might or might not have a batchentry associated
      if (notReturnBatchEntriesIds.length <= 1) {
        return [
          ...data,
          {
            batchEntry: notReturnBatchEntriesIds[0]
              ? batchEntriesData[notReturnBatchEntriesIds[0]]
              : null,
            kind: 'single',
            order,
          } as OrdersRenderData,
        ];
      }

      const batchEntriesSortedByRank = getBatchEntriesIdsSortedByRank(
        notReturnBatchEntriesIds,
        batchEntriesData,
      );

      // Split order. This for sure will have batch entries.
      return [
        ...data,
        { kind: 'partial-parent', order } as OrdersRenderData,
        ...batchEntriesSortedByRank.map(
          (beId) =>
            ({
              batchEntry: batchEntriesData[beId],
              kind: 'partial-child',
              order,
            }) as OrdersRenderData,
        ),
      ];
    }, []),
);

export const getTotalOrdersTableData = createSelector(getTotalOrders, (orders) => orders);

// Iterates through rates to buy to find if a rate is from an account that has an incomplete reg status
// Returns a mapping of carrier account object id to incomplete reg status for any found
export const getIncompleteRegStatusFromRates = createSelector(
  getRatesForBatchEntriesToBuy,
  getCarrierAccountRegStatusesIncomplete,
  (
    ratesToBuy: Rate[],
    regStatuses: { [carrierAccount: string]: IncompleteRegStatus },
  ): RegStatusByCarrierAccount => {
    const regStatusByCarrierAccount = ratesToBuy.reduce((acc, rate) => {
      const carrierAccountObjectId = rate.carrierAccount;
      const regStatus = regStatuses[carrierAccountObjectId];

      if (regStatus) return { ...acc, [carrierAccountObjectId]: regStatus };
      else return acc;
    }, {});

    return regStatusByCarrierAccount;
  },
);

// This is a selector to make the transition from order being the primary focus, to batch entry.
// Should be deprecated after that work is finished

// This is a selector to make the transition from order being the primary focus, to batch entry.
// Should be deprecated after that work is finished

export const getFailedAddressValidationMessages = createSelector(
  getFailedBatchEntriesMessages,

  (apiMessagesByOrderId) =>
    Object.entries(apiMessagesByOrderId)
      .filter(
        ([, apiMessages]) =>
          !!apiMessages.filter((apiMessage) => apiMessage.code === FAILED_ADDRESS_VALIDATION)
            .length,
      )
      .map(([key, apiMessages]) => ({
        errorMessages: apiMessages,
        orderId: key,
      })),
);

export const getUnpurchasedBatchEntriesOrderSelectionWithExpiredShipmentDate = createSelector(
  getBatchEntryData,
  getShipmentsData,
  getOrdersData,
  getIsUserAddressValid,
  (
    batchEntryData,
    shipmentsData,
    ordersData,
    isUserSenderAddressValid,
  ): Record<OrderObjectId, BatchEntryId[]> => {
    const unpurchasedBatchEntriesWithExpiredShipmentDate: Record<OrderObjectId, BatchEntryId[]> =
      {};
    Object.values(batchEntryData).forEach((batchEntry) => {
      if (
        [BatchEntryStatus.COMPLETE_SHIPMENT, BatchEntryStatus.PENDING].includes(
          batchEntry.objectStatus,
        )
      ) {
        let isUnpurchasedBatchEntryWithExpiredShipmentDate = false;

        // Checks batch entry shipment date
        if (batchEntry.shipmentDate?.isBefore(moment(), 'day'))
          isUnpurchasedBatchEntryWithExpiredShipmentDate = true;
        // when batchEntry.shipmentDate is null, we could check shipment.submissionDate
        else if (
          batchEntry.shipmentObjectId &&
          shipmentsData[batchEntry.shipmentObjectId]?.shipmentDate.isBefore(moment(), 'day')
        )
          isUnpurchasedBatchEntryWithExpiredShipmentDate = true;
        // batch entry was invalid for checkout so the shipment might be nulled out (done in Armor)
        else if (
          batchEntry.objectStatus === BatchEntryStatus.PENDING &&
          batchEntry.parcel &&
          ordersData[batchEntry.orderObjectId] &&
          !hasRatingErrors(
            ordersData[batchEntry.orderObjectId],
            batchEntry,
            null,
            null,
            [],
            isUserSenderAddressValid,
          ).hasErrors
        )
          isUnpurchasedBatchEntryWithExpiredShipmentDate = true;

        // aggregate the entry into the returned record
        if (isUnpurchasedBatchEntryWithExpiredShipmentDate) {
          if (unpurchasedBatchEntriesWithExpiredShipmentDate[batchEntry.orderObjectId]) {
            unpurchasedBatchEntriesWithExpiredShipmentDate[batchEntry.orderObjectId].push(
              batchEntry.id,
            );
          } else {
            unpurchasedBatchEntriesWithExpiredShipmentDate[batchEntry.orderObjectId] = [
              batchEntry.id,
            ];
          }
        }
      }
    });
    return unpurchasedBatchEntriesWithExpiredShipmentDate;
  },
);

export const getShipmentsWithExpiredShipmentDatesFromOrderSelection = createSelector(
  getBatchEntriesDataForSelectedOrders,
  getShipmentsData,
  (batchEntries, shipmentData): Shipment[] => {
    try {
      return batchEntries
        .map(({ shipmentObjectId }) => {
          if (!shipmentObjectId) return undefined;
          const shipment = shipmentData[shipmentObjectId];
          if (!shipment) return undefined;
          if (!shipment.shipmentDate) return undefined;
          const shipmentDateMoment = moment(shipment.shipmentDate);
          if (!shipmentDateMoment.isValid()) {
            newRelic.log(`Invalid shipment date for shipment ${shipmentObjectId}`, {
              level: 'WARN',
            });
            return undefined;
          }
          const twentyFourHoursAgo = moment().subtract(24, 'hours');
          return shipmentDateMoment.isBefore(twentyFourHoursAgo) ? shipment : undefined;
        })
        .filter((shipment): shipment is Shipment => Boolean(shipment));
    } catch (error) {
      newRelic.noticeError('Failed to select shipments with expired shipment date', {
        error,
        location: 'getShipmentsWithExpiredShipmentDatesFromOrderSelection',
      });
      return [];
    }
  },
);

const getOrderSelectionFromProps = (_: ApplicationState, orderSelection: OrderSelection) =>
  orderSelection;

const getBatchEntriesByOrderSelection = createSelector(
  getBatchEntryData,
  getOrderSelectionFromProps,
  (batchEntryData, orderSelection): BatchEntryExtended[] => {
    const batchEntries: BatchEntryExtended[] = [];
    if (orderSelection) {
      Object.values(orderSelection).forEach((batchEntryIds) => {
        batchEntryIds.forEach((batchEntryId) => {
          const batchEntry = batchEntryData[batchEntryId];
          if (batchEntry) batchEntries.push(batchEntry);
        });
      });
    }
    return batchEntries;
  },
);

export const getAreBatchEntriesFromOrderSelectCompleted = createSelector(
  getBatchEntriesByOrderSelection,
  (batchEntries): boolean =>
    batchEntries.length
      ? batchEntries.every(
          (batchEntry) =>
            batchEntry.objectStatus === BatchEntryStatus.COMPLETE_TRANSACTION ||
            batchEntry.objectStatus === BatchEntryStatus.COMPLETE_SHIPMENT ||
            batchEntry.objectStatus === BatchEntryStatus.SHIPMENT_REQUEST_FAILED,
        )
      : false,
);
